/**
 * halfpe-collection-grid.js
 * Noon-style category grid section — Shopify Prestige theme
 *
 * Features:
 *  • Desktop: single-row flex carousel OR wrapping CSS grid
 *  • Mobile:  2-row carousel (both rows scroll together) OR wrap grid
 *  • Prev/Next arrows scroll exactly `cols` items per click
 *  • Arrow disabled-state updates on scroll
 *  • Responds to window resize (mobile ↔ desktop switch)
 *  • Re-initialises on Shopify theme-editor section reload
 */

(function () {
  'use strict';

  /* ── Constants ──────────────────────────────────────────── */
  var MOBILE_BP = 750; // matches CSS breakpoint (749px max = 750px min)

  /* ── Helpers ────────────────────────────────────────────── */
  function isMobile() {
    return window.innerWidth < MOBILE_BP;
  }

  function int(val, fallback) {
    var n = parseInt(val, 10);
    return isNaN(n) ? fallback : n;
  }

  /**
   * Remove any .hcg__col-group wrappers that were added for mobile
   * layout and return all .hcg__item elements back to the track flat.
   */
  function flattenTrack(track) {
    var groups = track.querySelectorAll('.hcg__col-group');
    if (!groups.length) return;

    // Collect items in DOM order
    var items = Array.prototype.slice.call(track.querySelectorAll('.hcg__item'));

    // Remove groups
    Array.prototype.forEach.call(groups, function (g) {
      track.removeChild(g);
    });

    // Re-append items flat, reset inline styles set by mobile layout
    items.forEach(function (item) {
      item.style.width   = '';
      item.style.flex    = '';
      item.style.maxWidth = '';
      track.appendChild(item);
    });
  }

  /* ── Arrow state ────────────────────────────────────────── */
  function updateArrows(shell) {
    var track = shell.querySelector('.hcg__track');
    var prev  = shell.querySelector('.hcg__arrow--prev');
    var next  = shell.querySelector('.hcg__arrow--next');
    if (!track || !prev || !next) return;

    var atStart = track.scrollLeft <= 2;
    var atEnd   = track.scrollLeft >= track.scrollWidth - track.clientWidth - 2;

    prev.disabled = atStart;
    next.disabled = atEnd;
  }

  /* ── Desktop layout ─────────────────────────────────────── */
  function applyDesktop(shell) {
    var track = shell.querySelector('.hcg__track');
    if (!track) return;

    var cols = int(shell.dataset.desktopCols, 9);
    var gap  = int(shell.dataset.desktopGap,  16);
    var mode = shell.dataset.desktopMode || 'carousel';

    // Ensure no mobile grouping remains
    flattenTrack(track);

    track.style.gap = gap + 'px';

    var itemW = 'calc((100% - ' + (cols - 1) + ' * ' + gap + 'px) / ' + cols + ')';
    var items = track.querySelectorAll('.hcg__item');

    items.forEach(function (item) {
      if (mode === 'carousel') {
        item.style.flex     = '0 0 ' + itemW;
        item.style.width    = itemW;
        item.style.maxWidth = itemW;
      } else {
        // grid mode — still set explicit width so items wrap correctly
        item.style.flex     = '0 0 ' + itemW;
        item.style.width    = itemW;
        item.style.maxWidth = itemW;
      }
    });

    updateArrows(shell);
  }

  /* ── Mobile layout ──────────────────────────────────────── */
  function applyMobile(shell) {
    var track = shell.querySelector('.hcg__track');
    if (!track) return;

    var cols = int(shell.dataset.mobileCols, 3);
    var gap  = int(shell.dataset.mobileGap,  10);
    var mode = shell.dataset.mobileMode || 'carousel';

    track.style.gap = gap + 'px';

    /* ── GRID mode ── wrap, no columns groups needed */
    if (mode === 'grid') {
      flattenTrack(track);
      var itemW = 'calc((100% - ' + (cols - 1) + ' * ' + gap + 'px) / ' + cols + ')';
      var items = track.querySelectorAll('.hcg__item');
      items.forEach(function (item) {
        item.style.flex     = '0 0 ' + itemW;
        item.style.width    = itemW;
        item.style.maxWidth = itemW;
      });
      updateArrows(shell);
      return;
    }

    /* ── CAROUSEL mode ── build 2-row column-group structure ──
     *
     * Algorithm:
     *   total items = N
     *   top half    = items[0 .. ceil(N/2)-1]
     *   bottom half = items[ceil(N/2) .. N-1]
     *
     *   For each index i in top half:
     *     create .hcg__col-group
     *       append topItems[i]
     *       if bottomItems[i] exists, append bottomItems[i]
     *     append group to track
     *
     *   The track is a horizontal flex container;
     *   each group is one "column" snap unit.
     *   Scrolling the track scrolls BOTH rows simultaneously.
     */

    // First flatten any previous grouping
    flattenTrack(track);

    var allItems = Array.prototype.slice.call(track.querySelectorAll('.hcg__item'));
    if (!allItems.length) return;

    var half    = Math.ceil(allItems.length / 2);
    var topRow  = allItems.slice(0, half);
    var botRow  = allItems.slice(half);

    // Width for each column group = 1 col wide
    var groupW = 'calc((100% - ' + (cols - 1) + ' * ' + gap + 'px) / ' + cols + ')';

    var fragment = document.createDocumentFragment();

    topRow.forEach(function (topItem, i) {
      var group = document.createElement('div');
      group.className       = 'hcg__col-group';
      group.style.width     = groupW;
      group.style.minWidth  = groupW;
      group.style.maxWidth  = groupW;
      group.style.gap       = gap + 'px';

      // Reset item styles — they fill 100% of group width via CSS
      topItem.style.flex     = '';
      topItem.style.width    = '';
      topItem.style.maxWidth = '';
      group.appendChild(topItem);

      if (botRow[i]) {
        var botItem = botRow[i];
        botItem.style.flex     = '';
        botItem.style.width    = '';
        botItem.style.maxWidth = '';
        group.appendChild(botItem);
      }

      fragment.appendChild(group);
    });

    // Clear track and insert groups
    while (track.firstChild) {
      track.removeChild(track.firstChild);
    }
    track.appendChild(fragment);

    updateArrows(shell);
  }

  /* ── Main layout dispatcher ─────────────────────────────── */
  function applyLayout() {
    document.querySelectorAll('.hcg__shell').forEach(function (shell) {
      if (isMobile()) {
        applyMobile(shell);
      } else {
        applyDesktop(shell);
      }
    });
  }

  /* ── Arrow click handler ────────────────────────────────── */
  function initArrows() {
    document.querySelectorAll('.hcg__arrow').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var trackId = btn.dataset.target;
        var track   = document.getElementById(trackId);
        if (!track) return;

        var shell  = track.closest('.hcg__shell');
        var mobile = isMobile();

        var cols = int(
          mobile ? shell.dataset.mobileCols : shell.dataset.desktopCols,
          mobile ? 3 : 9
        );
        var gap = int(
          getComputedStyle(track).gap || (mobile ? '10' : '16'),
          mobile ? 10 : 16
        );

        // On mobile carousel we snap by col-group; on desktop by item
        var snapEl = mobile
          ? track.querySelector('.hcg__col-group')
          : track.querySelector('.hcg__item');

        if (!snapEl) return;

        // Scroll exactly `cols` snap units per click
        var scrollAmt = cols * (snapEl.offsetWidth + gap);
        var dir       = btn.classList.contains('hcg__arrow--prev') ? -1 : 1;

        track.scrollBy({ left: dir * scrollAmt, behavior: 'smooth' });
      });
    });

    // Update arrow disabled-state on scroll
    document.querySelectorAll('.hcg__track').forEach(function (track) {
      var shell = track.closest('.hcg__shell');
      if (!shell) return;
      track.addEventListener('scroll', function () {
        updateArrows(shell);
      }, { passive: true });
    });
  }

  /* ── Resize (debounced) ─────────────────────────────────── */
  var _resizeTimer = null;

  function initResize() {
    window.addEventListener('resize', function () {
      clearTimeout(_resizeTimer);
      _resizeTimer = setTimeout(applyLayout, 160);
    });
  }

  /* ── Bootstrap ──────────────────────────────────────────── */
  function init() {
    applyLayout();
    initArrows();
    initResize();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Shopify theme editor: re-run on section/block changes
  document.addEventListener('shopify:section:load',   applyLayout);
  document.addEventListener('shopify:block:select',   applyLayout);
  document.addEventListener('shopify:block:deselect', applyLayout);

})();